import React, { useState } from "react";
import { DataTable, DataTableColumnHeader, Button } from "@jem-open/jem-ui";
import { ColumnDef } from "@tanstack/react-table";
import { Download } from "lucide-react";
import McmAuditEntry from "../services/model/McmAuditEntry";
import { McmAuditLogApiService } from "../services/api/McmSupportApiService";

type AuditLogPanelProps = {
  auditEntries: McmAuditEntry[];
  isLoading: boolean;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onGoBack: () => void;
};

const escapeCSVField = (field: string): string => {
  const sanitized = /^[=+\-@]/.test(field) ? `'${field}` : field;
  if (sanitized.includes(",") || sanitized.includes("\n") || sanitized.includes('"')) {
    return `"${sanitized.replace(/"/g, '""')}"`;
  }
  return sanitized;
};

const generateAuditCSV = (entries: McmAuditEntry[]): string => {
  const headers = ["Date", "User", "Activity", "Segment / Announcement Name", "Companies"];
  const rows = [headers.join(",")];

  entries.forEach((entry) => {
    const date = entry.date_created
      ? new Date(entry.date_created).toLocaleString()
      : "";
    const user = entry.user
      ? `${entry.user.first_name} ${entry.user.last_name}`
      : "";
    const name = entry.details?.name || entry.details?.title || "";
    const companies = entry.target_companies?.length ?? 0;

    rows.push(
      [
        escapeCSVField(date),
        escapeCSVField(user),
        escapeCSVField(entry.action),
        escapeCSVField(name),
        String(companies),
      ].join(","),
    );
  });

  return rows.join("\n");
};

const downloadCSV = (content: string, filename: string): void => {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const auditColumns: ColumnDef<McmAuditEntry>[] = [
  {
    accessorKey: "date_created",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Date" />
    ),
    cell: ({ row }) => (
      <span className="text-sm text-greyscale-text-caption">
        {row.original.date_created
          ? new Date(row.original.date_created).toLocaleString()
          : "\u2014"}
      </span>
    ),
  },
  {
    id: "user",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="User" />
    ),
    cell: ({ row }) => (
      <span className="text-sm font-semibold text-greyscale-text-title">
        {row.original.user
          ? `${row.original.user.first_name} ${row.original.user.last_name}`
          : "\u2014"}
      </span>
    ),
  },
  {
    accessorKey: "action",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Activity" />
    ),
  },
  {
    id: "name",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Segment / Announcement Name" />
    ),
    cell: ({ row }) => (
      <span className="text-sm">
        {row.original.details?.name || row.original.details?.title || "\u2014"}
      </span>
    ),
  },
  {
    id: "companies",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Companies" />
    ),
    cell: ({ row }) => (
      <span className="text-sm">{row.original.target_companies?.length ?? 0}</span>
    ),
  },
];

const AuditLogPanel: React.FC<AuditLogPanelProps> = ({
  auditEntries,
  isLoading,
  page,
  totalPages,
  onPageChange,
  onGoBack,
}) => {
  const [isExporting, setIsExporting] = useState(false);

  const handleDownloadCsv = async (): Promise<void> => {
    setIsExporting(true);
    try {
      const pageSize = 100;
      const first = await McmAuditLogApiService({ page: 1, page_size: pageSize });
      let allEntries = [...first.results];

      if (first.pages > 1) {
        const remaining = await Promise.all(
          Array.from({ length: first.pages - 1 }, (_, i) =>
            McmAuditLogApiService({ page: i + 2, page_size: pageSize }),
          ),
        );
        remaining.forEach((r) => {
          allEntries = allEntries.concat(r.results);
        });
      }

      const csvContent = generateAuditCSV(allEntries);
      const today = new Date().toISOString().slice(0, 10);
      downloadCSV(csvContent, `mcm-audit-log-${today}.csv`);
    } catch {
      // HttpClientError toast is triggered by the service layer
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-md">
        <p className="text-base font-semibold text-greyscale-text-title">
          History Log
        </p>
        <Button
          variant="outline"
          leftIcon={<Download size={16} />}
          onClick={handleDownloadCsv}
          disabled={isExporting || isLoading}
        >
          {isExporting ? "Exporting..." : "Download CSV"}
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-greyscale-text-caption py-md">Loading audit log...</p>
      ) : (
        <DataTable
          columns={auditColumns}
          data={auditEntries}
          showPagination={false}
          showToolbar={false}
        />
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-xs mt-md">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
          >
            Previous
          </Button>
          <span className="text-sm text-greyscale-text-caption">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
          >
            Next
          </Button>
        </div>
      )}

      <div className="mt-md">
        <Button variant="outline" onClick={onGoBack}>
          Back to Messages
        </Button>
      </div>
    </div>
  );
};

export default AuditLogPanel;
